import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signInWithCustomToken } from "firebase/auth";
import { Notification } from "../core/notification_handler";
import { auth } from "../firebase/config";
import { vespidFunctionsAPI } from "../core/api";
import usePartnerDataHandler from "../hooks/partner_data_handler_hook";
import * as loadingAnimations from "../assets/animations/Loading.json";
import Lottie from "react-lottie";
import { authenticationAPI, PartnerConflictError } from "../core/authentication_handler";
import useDeviceTypeHandler from "../hooks/device_type_handler_hook";
import { ReactComponent as GoogleSignIn } from "../assets/icons/GoogleSignIn.svg";
import { ReactComponent as AppleSignIn } from "../assets/icons/AppleSignIn.svg";
import PasswordVisibility from "../components/PasswordVisibility";
import LoadingStatus from "../components/LoadingStatus";
import ConfirmPartnerDialog from "../components/ConfirmPartnerDialog";
import Button from "../components/Button";
import axios from "axios";
const Register = () => {
  const [passwordValidity, setPasswordValidity] = useState(" ");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [emailValidity, setEmailValidity] = useState("Email is required.");
  const [isLoading, setIsLoading] = useState(false);
  const [partnerConflict, setPartnerConflict] = useState<{
    currentPartner: { id: string; name: string };
    newPartnerId: string;
  } | null>(null);
  const [isReplacingPartner, setIsReplacingPartner] = useState(false);
  const { partnerData, isPartnerDataLoading, searchParams } = usePartnerDataHandler();
  const deviceType = useDeviceTypeHandler();
  const form = useRef();
  const navigate = useNavigate();

  const handlePartnerConflict = (currentPartner: { id: string; name: string }, newPartnerId: string) => {
    setPartnerConflict({ currentPartner, newPartnerId });
  };

  const handleConfirmPartnerReplace = async () => {
    if (!partnerConflict) return;
    
    setIsReplacingPartner(true);
    try {
      await authenticationAPI.completeOAuthWithReplacement(partnerConflict.newPartnerId);
      Notification.success("Partner association updated successfully!");
      setPartnerConflict(null);
      navigate("/install");
    } catch (error) {
      Notification.error("Failed to update partner association. Please try again.");
    } finally {
      setIsReplacingPartner(false);
    }
  };

  const handleCancelPartnerReplace = () => {
    setPartnerConflict(null);
    Notification.success("Your registration is successful! You can now use the Vespid app.");
    navigate("/install");
  };

  const checkEmail = (email: string): void => {
    const reg = new RegExp(
      /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i
    );
    if (email.length === 0) {
      setEmailValidity("Email is required.");
      return;
    } else if (!reg.test(email)) {
      setEmailValidity("Email is invalid.");
      return;
    }

    setEmailValidity("Email is valid.");
  };

  const checkPassword = (password: string, confirmPassword: string): void => {
    if (password.length === 0 || confirmPassword.length === 0) {
      setPasswordValidity("Password is required.");
    } else if (password !== confirmPassword) {
      setPasswordValidity("Password do not match.");
    } else {
      setPasswordValidity("Password match.");
    }
  };

  const togglePasswordVisibility = () => {
    const passwordInput = document.getElementById("passwordInput") as HTMLInputElement;
    const passwordInput2 = document.getElementById("passwordInput2") as HTMLInputElement;
    if (passwordInput.type === "password" || passwordInput2.type === "password") {
      passwordInput.type = "text";
      passwordInput2.type = "text";
    } else {
      passwordInput.type = "password";
      passwordInput2.type = "password";
    }
    setPasswordVisible(!passwordVisible);
  };

  const sendRegistration = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (passwordValidity === "Password do not match.") {
      Notification.error("Passwords do not match.");
      setIsLoading(false);
      return;
    }
    try {
      // Call backend register API
      const response = await vespidFunctionsAPI.postPublic("/account/register", {
        email: e.target.email.value,
        password: e.target.password.value,
      });

      // Sign in with custom token from backend
      if (!response.data?.custom_token) {
        throw new Error("Registration succeeded but no authentication token was returned. Please try again or contact support.");
      }
      await signInWithCustomToken(auth, response.data.custom_token);

      // Complete onboarding and tag to partner
      try {
        await vespidFunctionsAPI.post("/account/oauth/complete", {
          partner_id: partnerData?.id || undefined,
        });
        Notification.success("Your registration is successful! You can now use the Vespid app.");
        navigate("/install");
      } catch (oauthError: any) {
        // Handle 400 failed-precondition partner conflict
        if (axios.isAxiosError(oauthError) && 
            oauthError.response?.status === 400 && 
            oauthError.response?.data?.error?.code === 'failed-precondition') {
          const currentPartner = oauthError.response.data?.current_partner;
          if (currentPartner && partnerData?.id) {
            handlePartnerConflict(currentPartner, partnerData.id);
            return;
          }
        }
        throw oauthError;
      }
    } catch (error: any) {
      // Firebase auth errors have a code we can map to a friendly message.
      if (error?.code) {
        authenticationAPI.authErrorHandler(error.code);
      } else if (!axios.isAxiosError(error)) {
        // Non-Axios errors are not shown by the API interceptor, so surface them here.
        Notification.error(error?.message || "An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const googleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await authenticationAPI.googleSignIn(navigate, partnerData?.id);
    } catch (error: any) {
      if (error instanceof PartnerConflictError) {
        handlePartnerConflict(error.currentPartner, error.newPartnerId);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const appleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await authenticationAPI.appleSignIn(navigate, partnerData?.id);
    } catch (error: any) {
      if (error instanceof PartnerConflictError) {
        handlePartnerConflict(error.currentPartner, error.newPartnerId);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="font-primaryFont font-light flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="flex flex-col-reverse md:flex-row">
        {isPartnerDataLoading ? (
          <div className="max-w-sm md:max-w-md mx-auto">
            <Lottie
              options={{
                animationData: loadingAnimations,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              }}
              height={100}
              width={100}
              className="w-1/2 md:w-4/12"
            />
            <h1 className="mt-4 text-center text-2xl">
              Please wait while we are fetching your registration data...
            </h1>
          </div>
        ) : (
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <h1 className="mt-4 text-center text-2xl">
              Register your account here for {partnerData?.name || "the event"}
            </h1>
            <form
              id="register-form"
              ref={form}
              onSubmit={sendRegistration}
              method="POST"
              className="mt-4 flex flex-col justify-center items-center w-full border shadow-lg px-4 py-4 rounded-lg max-w-6 space-y-6"
            >
              <input
                type="email"
                name="email"
                placeholder="Email"
                required
                onChange={() => checkEmail(form.current.email.value)}
                data-testid="register-email"
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primaryYellow sm:text-sm/6"
              ></input>
              {emailValidity !== "Email is required." ? (
                <p
                  data-testid="register-email-validity"
                  className={`text-sm ${
                    emailValidity === "Email is valid."
                      ? "text-green-500"
                      : "text-red-500"
                  }`}
                >
                  {emailValidity}
                </p>
              ) : null}
              <div className="relative w-full">
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  required
                  onChange={() =>
                    checkPassword(
                      form.current.password.value,
                      form.current.password2.value
                    )
                  }
                  data-testid="register-password"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primaryYellow sm:text-sm/6"
                  id="passwordInput"
                />
                <button
                  type="button"
                  onClick={() => {
                    togglePasswordVisibility();
                  }}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 cursor-pointer"
                >
                  <PasswordVisibility hide={passwordVisible}/>
                </button>
              </div>

              <div className="relative w-full">
                <input
                  type="password"
                  name="password2"
                  placeholder="Repeat password"
                  required
                  onChange={() =>
                    checkPassword(
                      form.current.password.value,
                      form.current.password2.value
                    )
                  }
                  data-testid="register-password2"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primaryYellow sm:text-sm/6"
                  id="passwordInput2"
                />
                <button
                  type="button"
                  onClick={() => {
                    togglePasswordVisibility();
                  }}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 cursor-pointer"
                >
                  <PasswordVisibility hide={passwordVisible}/>
                </button>
              </div>

              <p
                data-testid="register-password-validity"
                className={`text-sm ${
                  passwordValidity === "Password match."
                    ? "text-green-500"
                    : "text-red-500"
                }`}
              >
                {passwordValidity}
              </p>
              <Button
                isDisabled={isLoading}
                htmlType="submit"
                variant="primary"
                type="standard"
                fullWidth
                data-testid="register-submit"
              >
                <LoadingStatus isLoading={isLoading} defaultText="Register"/>
              </Button>
              <div>
                {" "}
                ------------------------------ or ------------------------------{" "}
              </div>
              <div className="flex flex-col mt-4">
                <button
                  disabled={isLoading}
                  onClick={googleSignIn}
                  type="button"
                  className="flex w-full justify-center px-3  shadow-xs"
                >
                  <GoogleSignIn className="w-64 " />
                </button>
                {deviceType !== "android" ? (
                  <button
                    disabled={isLoading}
                    onClick={appleSignIn}
                    type="button"
                    className="flex w-full justify-center px-3  shadow-xs"
                  >
                    <AppleSignIn className="w-64 " />
                  </button>
                ) : null}
              </div>

              <p className="mt-2 font-light text-center">
                Already have an account?
                <Link
                  to={`/login?${searchParams}`}
                  rel="noreferrer noopener"
                  className=" text-primaryOrange"
                >
                  {" "}
                  Login
                </Link>{" "}
              </p>
            </form>
          </div>
        )}
      </div>
      
      <ConfirmPartnerDialog
        isOpen={partnerConflict !== null}
        currentPartnerName={partnerConflict?.currentPartner?.name || "Unknown Partner"}
        newPartnerName={partnerData?.name || "New Partner"}
        onConfirm={handleConfirmPartnerReplace}
        onCancel={handleCancelPartnerReplace}
        isLoading={isReplacingPartner}
      />
    </div>
  );
};

export default Register;
